import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
  useLayoutEffect,
} from "react";
import { createPortal } from "react-dom";
import {
  FaBell,
  FaInbox,
  FaList,
  FaSync,
} from "react-icons/fa";

const formatDateTime = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleString();
};

const formatRelativeTime = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  const diffMs = Date.now() - date.getTime();
  const diffMinutes = Math.round(diffMs / (1000 * 60));
  if (Math.abs(diffMinutes) < 60) {
    if (diffMinutes === 0) return "agora";
    return `há ${Math.abs(diffMinutes)} min`;
  }
  const diffHours = Math.round(diffMinutes / 60);
  if (Math.abs(diffHours) < 48) {
    return `há ${Math.abs(diffHours)} h`;
  }
  const diffDays = Math.round(diffHours / 24);
  return `há ${Math.abs(diffDays)} d`;
};

export default function NotificationsBell({
  items,
  unreadCount,
  onOpenRequests,
  onMarkItemsRead,
  onRefresh,
}) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);
  const buttonRef = useRef(null);
  const menuRef = useRef(null);
  const [menuStyle, setMenuStyle] = useState(null);
  const [portalTarget, setPortalTarget] = useState(null);

  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    setPortalTarget(document.body);
    return undefined;
  }, []);

  const updateMenuPosition = useCallback(() => {
    if (!open || typeof window === "undefined" || !buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    const menuElement = menuRef.current;
    const menuWidth = menuElement?.offsetWidth || 320;
    const viewportWidth = window.innerWidth;
    const horizontalPadding = 16;
    const maxLeft = Math.max(viewportWidth - menuWidth - horizontalPadding, horizontalPadding);
    const preferredLeft = rect.right - menuWidth;
    const left = Math.min(Math.max(preferredLeft, horizontalPadding), maxLeft);
    setMenuStyle({
      position: "fixed",
      top: rect.bottom + 8,
      left,
      zIndex: 1000,
    });
  }, [open]);

  useLayoutEffect(() => {
    if (!open) return undefined;
    updateMenuPosition();
    if (typeof window === "undefined") return undefined;
    const handleResize = () => updateMenuPosition();
    window.addEventListener("resize", handleResize);
    window.addEventListener("scroll", handleResize, true);
    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("scroll", handleResize, true);
    };
  }, [open, updateMenuPosition]);

  useEffect(() => {
    if (!open) return undefined;
    if (typeof document === "undefined") return undefined;
    const handleClick = (event) => {
      if (!containerRef.current) return;
      const isInsideContainer = containerRef.current?.contains(event.target);
      const isInsideMenu = menuRef.current?.contains(event.target);
      if (!isInsideContainer && !isInsideMenu) {
        setOpen(false);
      }
    };
    const handleKey = (event) => {
      if (event.key === "Escape") {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("keydown", handleKey);
    };
  }, [open]);

  const enhancedItems = useMemo(
    () =>
      (items || []).map((item) => ({
        ...item,
        relativeTime: item.relativeTime || formatRelativeTime(item.receivedAt),
        formattedTime: item.formattedTime || formatDateTime(item.receivedAt),
      })),
    [items]
  );

  const visibleItems = useMemo(
    () => enhancedItems.slice(0, 3),
    [enhancedItems]
  );

  const toggle = () => {
    setOpen((prev) => {
      const next = !prev;
      if (!next) {
        setMenuStyle(null);
      }
      if (!prev && next && typeof onMarkItemsRead === "function") {
        const unreadIds = visibleItems.filter((item) => item.isNew).map((item) => item.id);
        if (unreadIds.length) {
          onMarkItemsRead(unreadIds);
        }
      }
      return next;
    });
  };

  const menuContent = (
    <div
      ref={menuRef}
      className="z-50 w-80 max-w-[85vw] rounded-2xl border border-slate-200/80 bg-white/95 p-4 text-slate-700 shadow-xl backdrop-blur"
    >
      <div className="mb-3 flex items-center justify-between">
        <div>
          <p className="text-sm font-semibold">Solicitações destinadas a você</p>
          <p className="text-xs text-slate-500">
            Receba alertas sempre que novas solicitações forem atribuídas.
          </p>
        </div>
        <button
          type="button"
          onClick={() => {
            if (typeof onRefresh === "function") {
              onRefresh();
            }
          }}
          className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-slate-200/70 text-slate-500 transition hover:border-blue-200 hover:text-blue-600"
          title="Atualizar notificações"
          aria-label="Atualizar notificações"
        >
          <FaSync className="text-sm" />
        </button>
      </div>
      {visibleItems.length ? (
        <ul className="space-y-2">
          {visibleItems.map((item) => (
            <li
              key={item.id}
              className={`rounded-xl border px-3 py-2 text-sm transition ${
                item.isNew
                  ? "border-blue-200/70 bg-blue-50/80 text-blue-800"
                  : "border-slate-200/70 bg-white text-slate-700"
              }`}
            >
              <div className="flex items-start gap-3">
                <span
                  className={`mt-1 inline-flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full ${
                    item.isNew ? "bg-blue-100 text-blue-700" : "bg-slate-100 text-slate-500"
                  }`}
                >
                  <FaList />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold">
                    {item.processName || `Solicitação #${item.id}`}
                  </p>
                  <p className="truncate text-xs text-slate-500">
                    Solicitação #{item.id}
                    {item.stepName ? ` • ${item.stepName}` : ""}
                  </p>
                  {item.highlightValue && (
                    <p className="truncate text-xs text-slate-600">
                      {item.highlightLabel ? `${item.highlightLabel}: ` : ""}
                      {item.highlightValue}
                    </p>
                  )}
                  {item.requester && (
                    <p className="truncate text-xs text-slate-500">Aberta por {item.requester}</p>
                  )}
                  {item.relativeTime && (
                    <p className="mt-1 text-xs text-slate-400" title={item.formattedTime || undefined}>
                      {item.relativeTime}
                    </p>
                  )}
                </div>
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <div className="flex flex-col items-center justify-center space-y-2 rounded-2xl border border-dashed border-slate-200/80 bg-slate-50/80 p-6 text-center text-slate-500">
          <FaInbox className="text-2xl" />
          <p className="text-sm font-medium">Nenhuma solicitação pendente para você.</p>
          <p className="text-xs text-slate-400">Quando houver novidades, elas aparecerão aqui automaticamente.</p>
        </div>
      )}
      <button
        type="button"
        onClick={() => {
          if (typeof onOpenRequests === "function") {
            onOpenRequests();
          }
          setOpen(false);
        }}
        className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-full bg-gradient-to-r from-blue-600 via-sky-500 to-sky-500 px-4 py-2 text-sm font-semibold text-white shadow transition hover:from-blue-700 hover:via-sky-600 hover:to-sky-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-300"
      >
        Ver solicitações
      </button>
    </div>
  );

  return (
    <div className="relative z-50" ref={containerRef}>
      <button
        type="button"
        onClick={toggle}
        className={`relative inline-flex h-10 w-10 items-center justify-center rounded-full border text-sm font-medium shadow-sm transition focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-300 ${
          unreadCount
            ? "border-blue-200/80 bg-blue-50 text-blue-700 hover:bg-blue-100"
            : "border-slate-200/80 bg-white/80 text-slate-600 hover:bg-slate-100"
        }`}
        aria-haspopup="true"
        aria-expanded={open}
        aria-label="Notificações de solicitações"
        ref={buttonRef}
      >
        <FaBell />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 inline-flex min-h-[1.25rem] min-w-[1.25rem] items-center justify-center rounded-full bg-blue-600 px-1 text-xs font-semibold text-white">
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
      </button>
      {open &&
        (portalTarget && menuStyle
          ? createPortal(
              <div style={menuStyle} className="pointer-events-auto">
                {menuContent}
              </div>,
              portalTarget
            )
          : (
              <div className="pointer-events-auto absolute right-0 mt-2">
                {menuContent}
              </div>
            ))}
    </div>
  );
}
